/**
 * ExaminationService 兼容性包装类
 * 提供旧 API 兼容的接口，内部使用新的 protobuf-ts 生成的客户端
 */
import { ExaminationServiceClient } from "../../api/base/examination/examination.client";
import { ExaminationService as ExaminationServiceType } from "../../api/base/examination/examination";
import { HttpRpcTransport } from "./HttpRpcTransport";
import { HttpResponse } from "../../assets/javascripts/http";

/**
 * 递归解包装所有 StringValue 类型
 */
function unwrapResponse(obj: any): any {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => unwrapResponse(item));
  }

  const result: any = {};
  for (const key in obj) {
    const value = obj[key];
    if (value && typeof value === 'object' && 'value' in value && Object.keys(value).length === 1) {
      // 这是一个包装类型（StringValue, Int32Value 等）
      result[key] = value.value;
    } else if (value && typeof value === 'object') {
      result[key] = unwrapResponse(value);
    } else {
      result[key] = value;
    }
  }
  return result;
}

/**
 * 转换字段名：将 camelCase 转换为 PascalCase
 */
function convertFieldNames(obj: any): any {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => convertFieldNames(item));
  }

  const result: any = {};
  for (const key in obj) {
    // 将 examineeResults 转换为 ExamineeResults
    const newKey = key === 'examineeResults' ? 'ExamineeResults' : 
                   key === 'examinationReportResults' ? 'examinationReportResults' :
                   key;
    result[newKey] = convertFieldNames(obj[key]);
  }
  return result;
}

/**
 * ExaminationService 包装类
 * 保持与旧代码相同的构造函数签名和方法调用方式
 */
export class ExaminationService {
  private client: ExaminationServiceClient;

  constructor(baseUrl: string, postFn: (url: string, headers: any, body: any) => Promise<HttpResponse>) {
    const transport = new HttpRpcTransport(baseUrl, postFn);
    this.client = new ExaminationServiceClient(transport);
  }

  // 包装所有方法，保持旧的 API 调用方式
  async getMyExamineeList(request: any) {
    const response = await this.client.getMyExamineeList(request).response;
    const unwrapped = unwrapResponse(response);
    return convertFieldNames(unwrapped);
  }

  async updateExaminee(request: any) {
    const response = await this.client.updateExaminee(request).response;
    return unwrapResponse(response);
  }

  async deleteExaminationRelationship(request: any) {
    const response = await this.client.deleteExaminationRelationship(request).response;
    return unwrapResponse(response);
  }

  async getExamineeById(request: any) {
    const response = await this.client.getExamineeById(request).response;
    return unwrapResponse(response);
  }

  async getExamineesExaminationReportList(request: any) {
    const response = await this.client.getExamineesExaminationReportList(request).response;
    return unwrapResponse(response);
  }

  async getExaminationReportByExamineeReportDetailId(request: any) {
    const response = await this.client.getExaminationReportByExamineeReportDetailId(request).response;
    return unwrapResponse(response);
  }

  async updateExaminationReportByExamineeReportDetailId(request: any) {
    const response = await this.client.updateExaminationReportByExamineeReportDetailId(request).response;
    return unwrapResponse(response);
  }

  async createExaminer(request: any) {
    const response = await this.client.createExaminer(request).response;
    return unwrapResponse(response);
  }

  async getExaminerByAccountId(request: any) {
    const response = await this.client.getExaminerByAccountId(request).response;
    return unwrapResponse(response);
  }

  async getMyAllExaminers(request: any) {
    const response = await this.client.getMyAllExaminers(request).response;
    return unwrapResponse(response);
  }

  async pageListExaminee(request: any) {
    const response = await this.client.pageListExaminee(request).response;
    return unwrapResponse(response);
  }

  async listExaminees(request: any) {
    const response = await this.client.listExaminee(request).response;
    return unwrapResponse(response);
  }

  async addExaminee(request: any) {
    const response = await this.client.addExaminee(request).response;
    return unwrapResponse(response);
  }

  async batchDeleteExaminationRelationshipByExaminerAccountId(request: any) {
    const response = await this.client.batchDeleteExaminationRelationshipByExaminerAccountId(request).response;
    return unwrapResponse(response);
  }

  async addExamineeByAccountId(request: any) {
    const response = await this.client.addExamineeByAccountId(request).response;
    return unwrapResponse(response);
  }

  async updateExaminationReport(request: any) {
    const response = await this.client.updateExaminationReport(request).response;
    return unwrapResponse(response);
  }

  async getExaminationReportById(request: any) {
    const response = await this.client.getExaminationReportById(request).response;
    return unwrapResponse(response);
  }
}
